import { useNavigate } from "react-router-dom";
import { Shield, Users, Key, Home, ArrowRight, CheckCircle, FileText, Lock, Calendar, Sparkles, Heart, HandHeart, AlertTriangle, ArrowLeft, PenLine } from "lucide-react";
import "@/styles/govtech.css";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const PillarCard = ({ icon: Icon, title, tagline, description, benefits, status, onClick, testId }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-lg shadow-sm border-2 ${status === "available" ? "border-gray-100 hover:border-[#00703C] hover:shadow-md cursor-pointer" : "border-gray-100 opacity-75"} transition-all p-6`}
    data-testid={testId}
  >
    <div className="flex items-start gap-4">
      <div className={`w-14 h-14 rounded-2xl ${status === "available" ? "bg-[#00703C]" : "bg-gray-300"} flex items-center justify-center flex-shrink-0`}>
        <Icon className="text-white" size={28} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-lg font-bold text-[#333333]">{title}</h3>
          {status === "available" ? (
            <span className="text-xs bg-[#00703C] text-white px-2 py-0.5 rounded-full">Ready</span>
          ) : (
            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Coming Soon</span>
          )}
        </div>
        <p className="text-[#00703C] font-medium text-sm mb-2">{tagline}</p>
        <p className="text-gray-600 text-sm mb-3">{description}</p>
        
        {benefits && (
          <ul className="space-y-1">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-gray-500">
                <CheckCircle size={12} className="text-[#00703C]" />
                {benefit}
              </li>
            ))}
          </ul>
        )}
        
        {status === "available" && (
          <div className="mt-4 flex items-center text-[#00703C] font-semibold text-sm group-hover:underline">
            Start Now <ArrowRight size={16} className="ml-1" />
          </div>
        )}
      </div>
    </div>
  </div>
);

export default function ResidentPortal() {
  const navigate = useNavigate();

  const pillars = [
    {
      icon: Shield,
      title: "Succession Rights",
      tagline: "Know where you stand",
      description: "Lost a loved one who was the tenant? Find out in 2 minutes if you have the right to stay in your home.",
      benefits: ["Understand the 12-month rule", "Get your evidence checklist", "Prepare for your housing officer meeting"],
      status: "available",
      path: "/succession-triage",
      testId: "pillar-succession"
    },
    {
      icon: Key,
      title: "Right to Buy",
      tagline: "Your path to ownership",
      description: "Dreaming of owning your home? See exactly how much discount you've earned and what your next steps are.",
      benefits: ["Calculate your discount (up to 70%)", "Mortgage readiness checklist", "Step-by-step buying guide"],
      status: "available",
      path: "/rtb-triage",
      testId: "pillar-rtb"
    },
    {
      icon: Users,
      title: "Mutual Exchange",
      tagline: "Find your perfect swap",
      description: "Need a bigger place? Or want to downsize and save money? Match with other tenants looking to swap.",
      benefits: ["See potential savings", "Match with compatible swappers", "Get your exchange pack ready"],
      status: "available",
      path: "/liveability-triage",
      testId: "pillar-exchange"
    },
    {
      icon: Calendar,
      title: "Tenancy Milestones",
      tagline: "Never miss a deadline",
      description: "Track important dates like your starter tenancy ending, RTB eligibility, and annual reviews.",
      benefits: ["Set SMS or WhatsApp reminders", "Action checklists for each milestone", "Protect your tenancy rights"],
      status: "available",
      path: "/milestone-guardian",
      testId: "pillar-guardian"
    },
    {
      icon: AlertTriangle,
      title: "Struggling With Rent?",
      tagline: "We're here to help",
      description: "Worried about falling behind? Get a template letter and links to free support services.",
      benefits: ["Professional letter template", "Links to Citizens Advice & Shelter", "Discretionary Housing Payment info"],
      status: "available",
      path: "/arrears-support",
      testId: "pillar-arrears"
    },
    {
      icon: PenLine,
      title: "AI Letter Writer",
      tagline: "Say it right, the first time",
      description: "Describe your issue in your own words. We'll turn it into a polite, formal letter you can send today.",
      benefits: ["7 common letter types supported", "AI drafts in UK English, polite but firm", "Download as PDF or copy to clipboard"],
      status: "available",
      path: "/letter-writer",
      testId: "pillar-letter-writer"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Home</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>

      {/* Hero Section - Warm & Inviting */}
      <section className="bg-white py-16 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#00703C] bg-opacity-10 text-[#00703C] px-4 py-2 rounded-full mb-6">
              <HandHeart size={18} />
              <span className="text-sm font-medium">Free help for social housing tenants</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6 leading-tight">
              Clear, confidential guidance about your home —<br />
              <span className="text-[#00703C]">without having to make the phone call.</span>
            </h1>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Housing rules can feel overwhelming. Whether you're worried about staying in your home after losing someone,
              curious about buying your place, or just want to understand your options —
              <strong> we're here to help you figure it out, calmly and quietly.</strong>
            </p>
          </div>

          {/* Trust Points - Friendly Style */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <Lock size={16} className="text-[#00703C]" />
              </div>
              <span className="text-sm">Anonymous — no account, no name</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <Sparkles size={16} className="text-[#00703C]" />
              </div>
              <span className="text-sm">Answers in about 2 minutes</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <FileText size={16} className="text-[#00703C]" />
              </div>
              <span className="text-sm">A PDF you can keep &amp; share</span>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Introduction */}
      <section className="py-12 bg-[#00703C]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8 text-white">
            <div className="w-20 h-20 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
              <Heart className="text-white" size={36} />
            </div>
            <div>
              <p className="text-xl leading-relaxed">
                "I spent 10 years as a Housing Officer. I saw too many people lose their homes simply because they didn't know 
                their rights or couldn't navigate the system. <strong>TenURes is the tool I wish every resident had.</strong>"
              </p>
              <p className="text-green-200 mt-3">— Built by someone who's been on the frontline</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Do You Need Help With? */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#333333] mb-3">What Do You Need Help With?</h2>
            <p className="text-gray-600">Pick what's on your mind — we'll guide you through it step by step.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {pillars.map((pillar) => (
              <PillarCard
                key={pillar.title}
                {...pillar}
                onClick={() => pillar.status === "available" && navigate(pillar.path)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Simple */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#333333] text-center mb-12">How It Works</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-bold text-[#333333] mb-2">Answer a Few Simple Questions</h3>
              <p className="text-gray-600 text-sm">No jargon. No confusing forms. Just plain English. We never ask for your name or address.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-bold text-[#333333] mb-2">Get Your Guidance Summary</h3>
              <p className="text-gray-600 text-sm">We'll tell you where you stand and exactly what to prepare — in a calm, clear way.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-bold text-[#333333] mb-2">Walk In Prepared &amp; Confident</h3>
              <p className="text-gray-600 text-sm">Download a professional summary. When you do speak to your housing team, you'll be ready.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Talk Section */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white border border-gray-200 p-8 rounded-lg">
            <h2 className="text-xl font-bold text-[#333333] mb-6">Real Talk: Why This Matters</h2>
            
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>If you've just lost a family member</strong> who was the tenant, you might be terrified about 
                losing your home too. The Council has rules about who can stay — but most people don't know them. 
                We'll help you understand if you qualify and what proof you need.
              </p>
              <p>
                <strong>If you're thinking about buying your home</strong>, you probably have questions. 
                How much discount do I get? Can I afford it? What's the process? We'll calculate your 
                exact discount and give you a step-by-step guide.
              </p>
              <p>
                <strong>If your home doesn't fit anymore</strong> — maybe the kids have moved out, or you need more space — 
                swapping with another tenant could be your answer. We'll help you find matches and handle the paperwork.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Reassurance */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Lock className="mx-auto mb-4 text-[#00703C]" size={32} />
          <h3 className="font-bold text-[#333333] mb-2">Your Privacy is Protected</h3>
          <p className="text-gray-600 text-sm max-w-2xl mx-auto">
            We don't store your personal information. Everything happens in your browser, and when you close the page, 
            it's gone. The PDF you download? That's yours alone — we don't keep a copy.
          </p>
          <button 
            onClick={() => navigate('/privacy')}
            className="text-[#00703C] font-medium text-sm mt-3 hover:underline"
          >
            Read our full privacy commitment →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <TenuresLogo />
              <p className="text-gray-500 text-sm mt-4">
                Helping social housing tenants understand their rights since 2025.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] mb-4">Get Help With</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><button onClick={() => navigate('/succession-triage')} className="hover:text-[#00703C]">Succession Rights</button></li>
                <li><button onClick={() => navigate('/rtb-triage')} className="hover:text-[#00703C]">Right to Buy</button></li>
                <li><button onClick={() => navigate('/liveability-triage')} className="hover:text-[#00703C]">Mutual Exchange</button></li>
                <li><button onClick={() => navigate('/milestone-guardian')} className="hover:text-[#00703C]">Tenancy Milestones</button></li>
                <li><button onClick={() => navigate('/arrears-support')} className="hover:text-[#00703C]">Struggling With Rent?</button></li>
                <li><button onClick={() => navigate('/letter-writer')} className="hover:text-[#00703C]">AI Letter Writer</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#333333] mb-4">About</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><button onClick={() => navigate('/about')} className="hover:text-[#00703C]">Our Story</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-[#00703C]">Privacy</button></li>
                <li><button onClick={() => navigate('/')} className="hover:text-[#00703C]">For Housing Providers</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Built with a decade of frontline housing experience
            </p>
            <p className="text-xs text-gray-400 mt-2">
              This is guidance only, not legal advice. Always check with your Housing Officer for your specific situation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
